import type { MoneyString } from "@ibada/shared-types/finance"

import type { CurrencyCode, MoneyText } from "@/features/finance/model/finance-types"
import { http } from "@/shared/lib/api/http"

/** Scope shape shared by finance-resource report endpoints (mirrors the accounting queries). */
export interface FinanceResourceScope {
  workspaceId: string
  branchId: string | null
  scope: string
}

export interface ReconciliationSummaryResponse {
  totalAccounts: number
  reconciled: number
  pending: number
  openSessions: number
  oldestUnreconciledDate: string | null
}

/**
 * The three money figures are canonical decimal STRINGS at the response's `currency` scale (IBA-116) — they
 * always were on the wire; this type used to claim they were floats. `utilizationPct` stays a number: a
 * percentage is a quotient, not money.
 */
export interface FundUtilizationRow {
  fundId: string
  fundName: string
  received: MoneyText
  spent: MoneyText
  available: MoneyText
  utilizationPct: number
  restricted: boolean
}

export interface FundUtilizationResponse {
  items: FundUtilizationRow[]
  /** The scale every money string above is denominated at — the branch's accounting currency. */
  currency: CurrencyCode | null
}

export interface FundActivityMovement {
  date: string
  reference: string | null
  type: "INCOME" | "EXPENSE" | "OPENING"
  amount: MoneyText
}

export interface FundActivityResponse {
  fundId: string
  fundName: string
  /** The scale every money string on this payload is denominated at (IBA-116). */
  currency: CurrencyCode | null
  opening: MoneyText
  received: MoneyText
  spent: MoneyText
  closing: MoneyText
  movements: FundActivityMovement[]
}

export interface ActivityBudgetLine {
  lineId: string
  title: string
  type: "INCOME" | "EXPENSE"
  planned: MoneyString
  actual: MoneyString
  variance: MoneyString
}

/**
 * Activity budget performance (IBA-116): money is a canonical decimal string, and `currency` states what those
 * strings are denominated in. `formatReportValue` hands the string to `Intl` exactly as it arrived.
 */
export interface ActivityBudgetPerformanceResponse {
  budgetId: string
  budgetTitle: string
  activityId: string
  status: string
  currency: string
  lines: ActivityBudgetLine[]
  totals: {
    plannedIncome: MoneyString
    actualIncome: MoneyString
    plannedExpenses: MoneyString
    actualExpenses: MoneyString
  }
}

export interface DepartmentSummaryRow {
  departmentId: string
  departmentName: string
  income: number
  expense: number
  net: number
}

export interface DepartmentSummaryResponse {
  items: DepartmentSummaryRow[]
}

function scopeParams(scope: FinanceResourceScope) {
  return { workspaceId: scope.workspaceId, branchId: scope.branchId ?? undefined, scope: scope.scope }
}

export const financeResourceApi = {
  reconciliationSummary: async (scope: FinanceResourceScope): Promise<ReconciliationSummaryResponse> => {
    const response = await http.get<ReconciliationSummaryResponse>("/finance/reconciliation/summary", {
      params: scopeParams(scope),
    })
    return response.data
  },
  fundUtilization: async (scope: FinanceResourceScope): Promise<FundUtilizationResponse> => {
    const response = await http.get<FundUtilizationResponse>("/finance/funds/utilization", {
      params: scopeParams(scope),
    })
    return response.data
  },
  fundActivity: async (
    scope: FinanceResourceScope,
    input: { fundId: string; dateFrom: string; dateTo: string },
  ): Promise<FundActivityResponse> => {
    const response = await http.get<FundActivityResponse>(`/finance/funds/${encodeURIComponent(input.fundId)}/activity`, {
      params: { workspaceId: scope.workspaceId, dateFrom: input.dateFrom, dateTo: input.dateTo },
    })
    return response.data
  },
  activityBudgetPerformance: async (
    scope: FinanceResourceScope,
    input: { activityId: string },
  ): Promise<ActivityBudgetPerformanceResponse> => {
    // The budgeting routes wrap payloads in a { data, meta } envelope, unlike the other finance endpoints.
    const response = await http.get<{ data: ActivityBudgetPerformanceResponse }>("/finance/budgets/activity-performance", {
      params: { workspaceId: scope.workspaceId, activityId: input.activityId },
    })
    return response.data.data
  },
  departmentSummary: async (
    scope: FinanceResourceScope,
    input: { dateFrom?: string; dateTo?: string },
  ): Promise<DepartmentSummaryResponse> => {
    const response = await http.get<DepartmentSummaryResponse>("/finance/departments/summary", {
      params: { ...scopeParams(scope), dateFrom: input.dateFrom, dateTo: input.dateTo },
    })
    return response.data
  },
}
